import { calculatePlanPrice, applyDiscount } from "../../_utils/pricing";
import { withDerivedTierBoundaries } from "./TierTable";

const TIERED_MODELS = ["Volume", "Tiered"];

let idCounter = 0;
const generateId = () => `hydrated-item-${Date.now()}-${idCounter++}`;

/**
 * The exact inverse of `toSubmitShape.js`'s `toBillingSubmitPayload` - takes
 * a saved Subscription/Proposal/Invoice/UpsellDownsell record (nested
 * `accordionData` shape) and produces the flat shape `usePricingState(
 * initial)` already knows how to consume (that hook has accepted an
 * `initial` argument since it was written, but nothing has ever called it
 * with one - this is the first real hydration consumer).
 *
 * Exists because every "edit" flow in this app currently calls
 * `usePricingState()` with no argument, so editing a Subscription/Proposal/
 * Invoice/Upsell shows a blank pricing picker instead of the record's real
 * saved plans/products - a real, separate gap from the save-time corruption
 * bug `toSubmitShape.js` fixed (see docs/known-issues.md).
 *
 * `planCatalog`/`productCatalog` are OPTIONAL - only needed for records
 * whose own plan/product sub-schema doesn't persist a `pricingCycle`/
 * `billingCycle` at all (`UpsellDownsell`'s does not, confirmed by reading
 * the schema - unlike `Subscription`'s, which spreads the full
 * `planSchema.obj` and so always carries it). Without the real pricing
 * cycle, `calculatePlanPrice`'s cycle-conversion silently no-ops (falls
 * back to treating the item as already matching the selected cycle) -
 * wrong, silent pricing math on edit for any plan/product whose own cycle
 * differs from the record's billing cycle. When provided, a catalog is
 * used only as this one fallback (matched by `entityId`); Subscription/
 * Proposal/Invoice records never need it since they already carry the
 * field.
 */
export function fromBillingSubmitPayload(record, { selectedBillingCycle, planCatalog, productCatalog } = {}) {
  const cycle = selectedBillingCycle || record?.billingCycle;
  return {
    plans: (record?.plans || []).map((item) => fromAccordionItem(item, cycle, "planName", planCatalog)),
    products: (record?.products || []).map((item) => fromAccordionItem(item, cycle, "productName", productCatalog)),
    lineItems: (record?.lineItems || []).map(fromLineItem),
    discount: fromDiscount(record?.discount),
    taxes: (record?.taxes || []).map(fromTax),
  };
}

// `selectedBundle._id` is only ever populated by the NEW engine's own submit
// path (toSubmitShape.js only sets it when the picker's own `bundleId` was
// set). A record saved by the OLD BillingItems engine instead embeds the
// FULL catalog plan document (Subscription.js spreads `...planSchema.obj`
// onto every saved plan) - which carries the plan's own permanent bundle
// assignment as a flat `bundle` field (a bare id, or a populated `{_id,...}`
// object depending on the query). Same fallback chain the old
// `PlanProductTable`'s bundle-recovery `useEffect` used
// (`typeof plan.bundle === "object" ? plan.bundle?._id : ... plan.bundle ||
// plan._id?.bundle`) - without it, every pre-existing subscription created
// before the new engine shipped would hydrate with no bundle at all, which
// then empties the entity dropdown's options (PlanProductPicker filters
// `entities` by `bundleId`) even though the plan itself is perfectly valid.
function recoverBundleId(item) {
  if (item?.selectedBundle?._id) return item.selectedBundle._id;
  if (item?.bundle) return typeof item.bundle === "object" ? item.bundle?._id : item.bundle;
  if (item?._id?.bundle) return typeof item._id.bundle === "object" ? item._id.bundle?._id : item._id.bundle;
  return "";
}

// nested accordionData item -> flat engine item. `nameField` picks which of
// the item's own name fields to prefer first (`planName` for plans,
// `productName` for products - see toSubmitShape.js's comment on why
// Subscription's product sub-schema specifically needs `productName`), but
// falls through every other name field a differently-shaped record (an
// older document saved before a naming fix, or a Proposal/Invoice/Upsell
// record which stores a plain `name`) might carry instead. As a last resort,
// looks the entity up in the catalog by its own `entityId` (same lookup
// `pricingCycle` below already does) and takes the catalog's name - some
// legacy Subscription documents carry NOTHING beyond `_id` on a plan/product
// sub-document at all (confirmed live against a real record), so the record
// itself has no name field to fall back through.
function fromAccordionItem(item, selectedBillingCycle, nameField, catalog) {
  const accordionData = item?.accordionData || {};
  const isTieredOrVolume = TIERED_MODELS.includes(item?.pricingModel);
  const entityId = item?._id?._id ?? item?._id ?? null;
  const catalogEntity = Array.isArray(catalog) ? catalog.find((e) => e._id === entityId) : null;
  const pricingCycle = item?.pricingCycle || item?.billingCycle || catalogEntity?.pricingCycle || "";
  const quantity = accordionData.quantity ?? 1;
  const discountType = accordionData.discountType || "Flat";
  const discount = accordionData.discount ?? "";

  // `unitPrice` is stored as 0 for Tiered/Volume (see toSubmitShape.js's
  // toAccordionItem) since a single per-unit rate doesn't apply there - the
  // tiers carry the real rate, so `amount` (only used by non-tiered pricing)
  // falls back to the pre-discount `price` instead, which is at least a
  // sane, non-crashing value rather than a hardcoded 0 hidden behind
  // "Tiered"/"Volume" branches everywhere downstream.
  const amount = isTieredOrVolume
    ? accordionData.price ?? item?.amount ?? item?.price ?? 0
    : accordionData.unitPrice ?? item?.amount ?? item?.price ?? 0;

  const pricingTiers = withDerivedTierBoundaries(
    (accordionData.pricingTiers || item?.pricingTiers || []).map((tier) => ({
      from: tier?.from !== undefined && tier?.from !== null ? String(tier.from) : "1",
      to: tier?.to !== undefined && tier?.to !== null ? String(tier.to) : "",
      unitPrice: tier?.unitPrice !== undefined && tier?.unitPrice !== null ? String(tier.unitPrice) : "0",
    })),
  );

  // `usePricingState` does NOT recompute plan/product prices (only line
  // items and taxes get re-derived) - so basePrice/totalPrice must be
  // computed here, via the SAME formula PlanProductPicker.priceItem uses,
  // so a hydrated row is indistinguishable from a freshly-edited one.
  const basePrice = calculatePlanPrice({
    pricingModel: item?.pricingModel,
    amount,
    pricingTiers,
    seats: quantity,
    currentPlanCycle: pricingCycle,
    selectedBillingCycle,
  });
  const totalPrice = applyDiscount(basePrice, { type: discountType, value: discount });

  return {
    id: generateId(),
    // A populated Mongo ref (`item._id` arriving as `{_id: ..., ...}`
    // rather than a bare id string) is a real shape some callers pass -
    // same defense the old `transformProduct` needed for
    // `product._id?._id || product._id`.
    entityId,
    bundleId: recoverBundleId(item),
    bundleName: item?.selectedBundle?.name ?? "",
    label:
      item?.[nameField] ??
      item?.name ??
      item?.planName ??
      item?.productName ??
      catalogEntity?.[nameField] ??
      catalogEntity?.name ??
      "",
    pricingModel: item?.pricingModel ?? null,
    amount,
    pricingTiers,
    pricingCycle,
    quantity,
    freeCredits: accordionData.freeCredits ?? item?.walletCredits ?? 0,
    discountType,
    discount,
    basePrice,
    value: totalPrice,
    totalPrice,
  };
}

function fromLineItem(item) {
  return {
    id: generateId(),
    label: item?.name ?? "",
    quantity: item?.quantity ?? 1,
    value: item?.value ?? "",
    discount: item?.discount ?? "",
    discountType: item?.discountType || "Flat",
    discountApplied: item?.discountApplied ?? true,
  };
}

// Every schema declares `discount` as an array of `{value, type}` - take
// its first entry back to the single object `usePricingState` holds it as.
function fromDiscount(discount) {
  const source = Array.isArray(discount) ? discount[0] : discount;
  return { type: source?.type || "Flat", value: source?.value ?? "" };
}

// `tax` (the schema's field name) -> `taxValue` (the engine's). `amount`
// (the pre-tax total it was calculated against) is dropped - the hook
// re-derives `totalTax` from the live pricing state, not the saved value.
function fromTax(tax) {
  return {
    id: generateId(),
    taxLabel: tax?.taxLabel ?? "",
    taxType: tax?.taxType || "Percent",
    taxValue: tax?.tax ?? tax?.taxValue ?? "",
  };
}
